import { useMemo, useState } from 'react';
import { Lock, Search } from 'lucide-react';
import { formatHubBranchLabel } from '../../utils/destinationType.js';
import Button from '../common/Button.js';

const DestinationLocationHubField = ({
  hubs = [],
  selectedIds = [],
  onChange,
  sourceLabel = '',
  sourceCode = '',
  disabled = false,
}) => {
  const [search, setSearch] = useState('');
  const selected = useMemo(
    () => new Set((Array.isArray(selectedIds) ? selectedIds : []).map(String)),
    [selectedIds],
  );

  const options = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (hubs || []).filter((hub) => {
      if (!q) return true;
      const label = `${formatHubBranchLabel(hub)} ${hub.location || ''} ${hub.location_code || ''} ${hub.hub_id || ''}`.toLowerCase();
      return label.includes(q);
    });
  }, [hubs, search]);

  const allIds = useMemo(
    () => (hubs || []).map((hub) => String(hub.id)).filter(Boolean),
    [hubs],
  );
  const visibleIds = useMemo(
    () => options.map((hub) => String(hub.id)).filter(Boolean),
    [options],
  );
  const visibleAllSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const visibleSomeSelected = visibleIds.some((id) => selected.has(id));

  const toggle = (hubId) => {
    if (disabled) return;
    const id = String(hubId);
    const next = selected.has(id)
      ? selectedIds.filter((item) => String(item) !== id)
      : [...selectedIds, hubId];
    onChange(next);
  };

  const selectAll = () => {
    if (disabled) return;
    if (search.trim()) {
      const keep = new Set(visibleIds);
      const rest = selectedIds.filter((item) => !keep.has(String(item)));
      onChange([...rest, ...options.map((hub) => hub.id)]);
      return;
    }
    onChange((hubs || []).map((hub) => hub.id));
  };

  const clearAll = () => {
    if (disabled) return;
    if (search.trim()) {
      const hide = new Set(visibleIds);
      onChange(selectedIds.filter((item) => !hide.has(String(item))));
      return;
    }
    onChange([]);
  };

  const toggleVisibleAll = () => {
    if (visibleAllSelected) clearAll();
    else selectAll();
  };

  const sourceText = sourceLabel
    ? `${sourceLabel}${sourceCode ? ` (${sourceCode})` : ''}`
    : 'This location hub';

  return (
    <div className="col-span-2 sm:col-span-4 min-w-0">
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 sm:p-4 space-y-3">
        <div>
          <p className="block text-xs sm:text-sm font-medium text-secondary mb-1.5">
            Source Location Hub
          </p>
          <div className="flex items-center gap-2 w-full px-3 sm:px-4 py-2 sm:py-2.5 border border-gray-200 rounded-xl bg-gray-100 text-sm sm:text-base text-gray-700">
            <Lock className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0 text-gray-500" />
            <span className="min-w-0 truncate font-medium">{sourceText}</span>
            <span className="ml-auto shrink-0 text-[10px] sm:text-xs font-semibold uppercase tracking-wide text-gray-500">
              Locked
            </span>
          </div>
        </div>

        <div>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
            <label className="block text-xs sm:text-sm font-medium text-secondary">
              Destination Location Hubs
            </label>
            <p className="text-[10px] sm:text-xs text-gray-500">
              {selected.size} of {allIds.length} selected
            </p>
          </div>

          <div className="grid grid-cols-2 gap-2 mb-2">
            <Button
              type="button"
              size="sm"
              variant={visibleAllSelected ? 'primary' : 'secondary'}
              onClick={selectAll}
              disabled={disabled || allIds.length === 0}
              className="w-full justify-center"
            >
              Select All
            </Button>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={clearAll}
              disabled={disabled || selected.size === 0 || !visibleSomeSelected}
              className="w-full justify-center"
            >
              Clear
            </Button>
          </div>

          <div className="rounded-xl border border-gray-300 bg-white overflow-hidden">
            <div className="p-2 border-b border-gray-200">
              <div className="relative min-w-0">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') e.preventDefault();
                  }}
                  placeholder="Search destination hubs..."
                  disabled={disabled}
                  autoComplete="off"
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-gray-100"
                />
              </div>
            </div>
            <div className="max-h-48 sm:max-h-64 overflow-y-auto overscroll-contain">
              {visibleIds.length > 0 && (
                <label className="flex items-center gap-2.5 px-3 py-2.5 sm:py-2 bg-gray-50 hover:bg-blue-50 cursor-pointer text-sm text-secondary font-semibold border-b border-gray-200 sticky top-0 z-10">
                  <input
                    type="checkbox"
                    checked={visibleAllSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = visibleSomeSelected && !visibleAllSelected;
                    }}
                    onChange={toggleVisibleAll}
                    disabled={disabled}
                    className="w-4 h-4 shrink-0 accent-blue-500"
                    aria-label="Select all destination hubs"
                  />
                  <span className="min-w-0">
                    {visibleAllSelected ? 'Deselect all' : 'Select all'}
                    {search.trim() ? ' (filtered)' : ''}
                  </span>
                </label>
              )}
              {options.map((hub) => {
                const id = String(hub.id);
                const label = formatHubBranchLabel(hub) || hub.location || hub.hub_id;
                return (
                  <label
                    key={id}
                    className="flex items-center gap-2.5 px-3 py-2.5 sm:py-2 hover:bg-blue-50 cursor-pointer text-sm text-gray-800 border-b border-gray-50 last:border-b-0"
                  >
                    <input
                      type="checkbox"
                      checked={selected.has(id)}
                      onChange={() => toggle(hub.id)}
                      disabled={disabled}
                      className="w-4 h-4 shrink-0 accent-blue-500"
                    />
                    <span className="min-w-0 truncate">{label}</span>
                    {hub.location_code ? (
                      <span className="ml-auto shrink-0 font-mono text-[10px] sm:text-xs text-gray-500">
                        {hub.location_code}
                      </span>
                    ) : null}
                  </label>
                );
              })}
              {options.length === 0 && (
                <p className="px-3 py-3 text-sm text-gray-400">
                  {hubs.length === 0
                    ? 'No other location hubs yet'
                    : 'No hubs match this search'}
                </p>
              )}
            </div>
          </div>
          <p className="mt-1.5 text-[10px] sm:text-xs text-gray-500">
            Tick one hub or use Select All. Booking and ML creation will only show these destinations from this source hub.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DestinationLocationHubField;
